import { ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import React, {
  createContext,
  memo,
  Suspense,
  useContext,
  useState
} from 'react'

import { ClientFeatures } from './ClientFeatures'

type AccessibilityContextType = {
  accessibilityOn: boolean
  accessibilityToggle: () => void
}

export const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined)

export const useAccessibilityContext = () => {
  const context = useContext(AccessibilityContext)
  if (!context) {
    throw new Error('useAccessibilityContext must be used within ThemeProvider')
  }
  return context
}

export type Theme = typeof theme

export const theme = {
  colors: {
    variants: {
      neutralLight4: '#FFFFFF',
      neutralLight3: '#E5E5E5',
      neutralLight2: '#E3E3E3',
      neutralDark7: '#707070',
      neutralDark6: '#3D4246',
      neutralDark5: '#2F2F2F',
      neutralDark4: '#2B2A2A',
      neutralDark3_1: '#262527',
      neutralDark3: '#222222',
      neutralDark2: '#141B35',
      neutralDark1: '#000000',
      primaryDark: '#324069',
      primaryLight: '#0065FF',
      primaryLight2: '#5c96d1',
      secondaryDark: '#4D9BA8',
      secondaryLight: '#2DA1BA',
      tertiaryDark: '#9242D6',
      tertiaryLight: '#9E3CDE',
      buttonGradient1: '#0A86B8',
      buttonGradient2: '#600FF5',
      buttonGradient3: '#891ADD',
      dangerLight: '#CC2121'
    }
  },
  fontFamily: {
    heading: "'DM Serif Display', serif",
    paragraph: "'Plus Jakarta Sans', sans-serif"
  }
}

interface Props {
  children: any
  fallback?: any
  pageContext: any
}

export const ThemeProvider = memo(({ children, fallback }: Props) => {
  const [accessibilityOn, setAccessibilityOn] = useState(false)
  const accessibilityToggle = () => setAccessibilityOn((prev) => !prev)

  return (
    <EmotionThemeProvider theme={theme}>
      <AccessibilityContext.Provider
        value={{ accessibilityOn, accessibilityToggle }}
      >
        <Suspense fallback={fallback}>
          <ClientFeatures>{children}</ClientFeatures>
        </Suspense>
      </AccessibilityContext.Provider>
    </EmotionThemeProvider>
  )
})
